/**
 * Single hit returned by the `search_chats` tool. `kind` distinguishes how
 * the chat matched: "message" hits anchor to a specific turn (`messageId`
 * and `role` are set); "title" hits surfaced via the chat title alone
 * (`messageId` and `role` are absent).
 */
export interface ChatSearchHit {
  kind: "message" | "title";
  chatId: string;
  messageId?: string;
  role?: "user" | "assistant";
  chatTitle: string;
  /** HTML: the matched terms wrapped in `<mark>`, every other character escaped. */
  snippet: string;
  createdAt: string;
  score: number;
}

/**
 * JSON payload emitted by the backend on a `MessageType.CHAT_SEARCH_RESULTS`
 * block. `gateDenied` is only present when the workspace plan blocks the tool.
 */
export interface ChatSearchResultsPayload {
  toolName: "search_chats";
  version: 1;
  toolId: string;
  query: string;
  hits: ChatSearchHit[];
  gateDenied?: string;
}

const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

// Postgres' headline copies the indexed message or title verbatim and only
// inserts literal <mark> tags, so the raw snippet carries any markup a project
// member typed. Both renderers set it as innerHTML; escaping it here leaves
// neither one to remember.
const escapeSnippet = (snippet: string): string =>
  snippet
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/&lt;mark&gt;/g, "<mark>")
    .replace(/&lt;\/mark&gt;/g, "</mark>");

const isChatSearchHit = (v: unknown): v is ChatSearchHit => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    (r.kind === "message" || r.kind === "title") &&
    isString(r.chatId) &&
    (r.messageId === undefined || isString(r.messageId)) &&
    (r.role === undefined || r.role === "user" || r.role === "assistant") &&
    isString(r.chatTitle) &&
    isString(r.snippet) &&
    isString(r.createdAt) &&
    isNumber(r.score)
  );
};

/**
 * Validates an unknown value against the chat-search payload shape. The
 * backend contract is fixed (see `pkg/go/domains/tools/tools/search_chats`),
 * so this guard mirrors what the emitter writes. Returns the payload on a
 * successful match or `null` otherwise — never throws. Snippets come back
 * escaped, safe to set as innerHTML.
 */
export const parseChatSearchResultsPayload = (v: unknown): ChatSearchResultsPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.toolName !== "search_chats") return null;
  if (r.version !== 1) return null;
  if (!isString(r.toolId)) return null;
  if (!isString(r.query)) return null;
  if (!Array.isArray(r.hits)) return null;
  if (!r.hits.every(isChatSearchHit)) return null;
  if (r.gateDenied !== undefined && !isString(r.gateDenied)) return null;
  return {
    toolName: "search_chats",
    version: 1,
    toolId: r.toolId,
    query: r.query,
    hits: r.hits.map((hit) => ({ ...hit, snippet: escapeSnippet(hit.snippet) })),
    gateDenied: r.gateDenied,
  };
};
